import type { HTMLProps, ReactElement } from 'react';
import { forwardRef } from 'react';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';

import Loader from '@app/components/Loader';
import { rem } from '@app/styles/theme/layout';

import {
  Container,
  fieldStyles,
  floatingStyles,
  invalidStyles,
  Label,
} from './common.styles';

const Input = styled.input`
  ${fieldStyles}
  ${invalidStyles}

  &:not(:placeholder-shown) + label,
  &:focus + label {
    ${floatingStyles}
  }
`;

const InputLoader = styled(Loader)`
  position: absolute;
  top: ${rem(20)};
  right: ${rem(18)};
`;

interface TextFieldProps extends Omit<HTMLProps<HTMLInputElement>, 'as'> {
  isValid?: boolean;
  formLabel: string | ReactElement;
  half?: boolean;
  loading?: boolean;
  labelProps?: Omit<HTMLProps<HTMLLabelElement>, 'as'>;
}

const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  ({ formLabel, isValid, half, loading, labelProps, ...props }, ref) => {
    const theme = useTheme();

    return (
      <Container half={half}>
        <Input
          /*
           * isValid is an optional prop so we check specifically against boolean
           */
          aria-invalid={isValid === false ? 'true' : 'false'}
          type="text"
          id={props.name}
          {...props}
          ref={ref}
          placeholder=" "
        />
        <Label {...labelProps} htmlFor={props.id || props.name}>
          {formLabel}
        </Label>
        {loading && <InputLoader color={theme.colors.labelGray} />}
      </Container>
    );
  },
);

TextField.displayName = 'TextField';

export default TextField;
